import React, { useState, useEffect } from 'react';
import { FormControlLabel, Stack, Switch, Typography, Chip, Box } from '@mui/material';
import CommonLayout from '../_layouts/CommonLayout';
import { AppContext } from '../../context';
import FeaturesManager from 'utils/features/FeaturesManager';
import type { Feature } from 'utils/features/FeaturesManager';
import { Environment, getEnv, isEnvironmentAllowed } from 'data_sources/constants';

// Helper function to get environment color
const getEnvColor = (
  env: Environment
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (env) {
    case 'dev':
      return 'info';
    case 'int':
      return 'primary';
    case 'qa':
      return 'secondary';
    case 'stage':
      return 'warning';
    case 'uat':
      return 'warning';
    case 'prod':
      return 'error';
    default:
      return 'default';
  }
};

const Features: React.FC = () => {
  const { isFeatureEnabled, setFeatureEnabled } = React.useContext(AppContext);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [features, setFeatures] = useState<Feature[]>([]);
  const currentEnv = getEnv();

  useEffect(() => {
    // Clean up any features that are no longer registered in code
    FeaturesManager.cleanupRemovedFeatures();
    // Only show managed features (features that still have flag control enabled)
    setFeatures(FeaturesManager.getInstance().getManagedFeatures());
  }, []);

  const handleFeatureChange = (featureId: string, isEnabled: boolean) => {
    setIsSaving(true);
    // Simulate API call delay
    setTimeout(() => {
      setFeatureEnabled(featureId, isEnabled);
      setFeatures(FeaturesManager.getInstance().getManagedFeatures());
      setIsSaving(false);
    }, 500);
  };

  const handleForceUpdate = () => {
    FeaturesManager.forceUpdateAllFeatures();
    setFeatures(FeaturesManager.getInstance().getManagedFeatures());
  };

  const handleDebugFeatures = () => {
    FeaturesManager.getInstance().debugFeatures();
  };

  const handleCleanupFeatures = () => {
    FeaturesManager.cleanupRemovedFeatures();
    setFeatures(FeaturesManager.getInstance().getManagedFeatures());
  };

  return (
    <CommonLayout
      headerProps={{
        title: 'Features',
      }}
    >
      <Stack spacing={2}>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant='h6'
            sx={{ mb: 1 }}
          >
            Current Environment:{' '}
            <Chip
              label={currentEnv.toUpperCase()}
              color={getEnvColor(currentEnv)}
              size='small'
            />
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
          >
            Only features allowed in the current environment are shown. Features with maxEnv restrictions are hidden in
            environments above their limit.
          </Typography>
        </Box>

        {features
          .filter((feature) => {
            const maxEnv = feature.maxEnv ?? 'qa';
            return isEnvironmentAllowed(maxEnv);
          })
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((feature) => {
            const maxEnv = feature.maxEnv ?? 'qa';
            const effectivelyEnabled = isFeatureEnabled(feature.id);

            return (
              <FormControlLabel
                key={feature.id}
                sx={{
                  opacity: isSaving ? 0.5 : 1,
                  pointerEvents: isSaving ? 'none' : 'auto',
                  width: 'fit-content',
                }}
                control={
                  <Switch
                    id={feature.id}
                    checked={effectivelyEnabled}
                    onChange={({ target }) => handleFeatureChange(feature.id, target.checked)}
                  />
                }
                label={
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 600 }}>{feature.name}</Typography>
                      <Chip
                        label={`Max: ${maxEnv.toUpperCase()}`}
                        color={getEnvColor(maxEnv)}
                        size='small'
                        variant='filled'
                      />
                    </Box>
                    <Typography variant='caption'>{feature.description}</Typography>
                  </Stack>
                }
              />
            );
          })}
      </Stack>
    </CommonLayout>
  );
};

export default Features;
